




















































import { Component } from 'vue-property-decorator';
import Vue from 'vue';
import EmulationProjectOptions from './EmulationProjectOptions.vue';
import { Get, Sync } from 'vuex-pathify';
import { ICreateEnvironmentPayload } from '@/types/Import';
import { ROUTES } from '@/router/routes.const';
import { IEnvironment, IEaasiResource } from '@/types/Resource';
import { getResourceId, getResourceArchiveId } from '@/helpers/ResourceHelper';
import ResourceSideBar from './ResourceSideBar.vue';
import ConfirmModal from '@/components/global/Modal/ConfirmModal.vue';
import { buildAccessInterfaceQuery } from '@/helpers/AccessInterfaceHelper';
import CreateBaseEnvModal from './base-environment/CreateBaseEnvModal.vue';
import EmulationProjectEnvironment from '@/models/emulation-project/EmulationProjectEnvironment';
import eventBus from '@/utils/event-bus';
import { generateNotificationError } from '@/helpers/NotificationHelper';

@Component({
	name: 'EmulationProjectScreen',
	components : {
		EmulationProjectOptions,
		CreateBaseEnvModal,
		ConfirmModal,
		ResourceSideBar
	}
})
export default class EmulationProjectScreen extends Vue {
	private clearProjectErrorMessage = 'There was a problem clearing emulation project, please try again.';
	private retrieveEmulationProjectErrorMessage = 'There was a problem retrieving emulation project environment.';
	private noRemoteEnvironmentsErrorMessage = 'Emulation project does not support remote environments.';
	private troubleRetrievingErrorMessage = 'There was a problem retrieving emulation project environment.';

	/* Computed
	============================================*/
	@Sync('emulationProject/createEnvironmentPayload')
	createEnvironmentPayload: ICreateEnvironmentPayload;

	@Sync('showLoader')
	showLoader: boolean;

	@Sync('emulationProject/selectedSoftwareId')
	selectedSoftwareId: string;

	@Sync('emulationProject/environment')
	environment: EmulationProjectEnvironment;

	@Sync('resource/activeEnvironment')
	activeEnvironment: IEnvironment;

	@Get('emulationProject/canRunProject')
	readonly canRunProject: boolean;

	@Get('emulationProject/projectEnvironments')
	environments: IEnvironment[];

	@Get('emulationProject/projectObjects')
	objects: IEaasiResource[];

	@Get('emulationProject/constructedFromBaseEnvironment')
	constructedFromBaseEnvironment: boolean;

	@Get('emulationProject/selectedObjects')
	selectedObjects: IEaasiResource[];

	get clearAllDisabled(): boolean {
		return this.environments.length === 0 && this.objects.length === 0;
	}

	/* Methods
	============================================*/
	clearAllAlertModal: boolean = false;

	/* Methods
	============================================*/
	async runEmulationProject() {
		try {
			const emulationProjectEnv = await this.prepareEmulationProject(this.environment);

			// Set newly create emulation project environment to active
			this.activeEnvironment = emulationProjectEnv;

			// Route to access interface screen
			this.$router.push(this.buildQuery(emulationProjectEnv.envId));
		} catch(e) {
			this.handleError(e);
		}
	}

	private async prepareEmulationProject(emuProjectEnv: EmulationProjectEnvironment): Promise<IEnvironment> {
		// just use selected environment as-is, without modifications
		const id = emuProjectEnv.envId;
		const environment: IEnvironment = await this.$store.dispatch('resource/getEnvironment', id);
		if (!environment) {
			throw new Error(this.troubleRetrievingErrorMessage);
		}

		return environment;
	}

	private buildQuery(envId: string) {
		return this.selectedObjects.length && !this.constructedFromBaseEnvironment
			? buildAccessInterfaceQuery({
				envId,
				archiveId: getResourceArchiveId(this.selectedObjects[0]),
				objectId: getResourceId(this.selectedObjects[0])
			})
			: buildAccessInterfaceQuery({ envId });
	}

	async init() {
		this.showLoader = true;
		await this.$store.dispatch('emulationProject/loadProject');
		if (this.environments.length === 1) {
			this.environment = new EmulationProjectEnvironment(this.environments[0]);
			this.$router.push(ROUTES.EMULATION_PROJECT.DETAILS);
		}
		if (!this.environment) {
			this.$router.push(ROUTES.EMULATION_PROJECT.OPTIONS);
		}
		this.showLoader = false;
	}

	prepareEnvironment(env: IEnvironment): IEnvironment {
		let emulationProjectEnvironment: IEnvironment = {
			...env,
			drives: this.environment.drives.map(d => d.drive),
			driveSettings: this.environment.drives
		};
		if (this.constructedFromBaseEnvironment) {
			emulationProjectEnvironment.driveSettings.forEach(d => {
				let selectedObject = this.objects.find(o => o.id === d.objectId);
				if (selectedObject) d.objectArchive = selectedObject.archiveId;
			});
		}
		return emulationProjectEnvironment;
	}

	handleError(err: string) {
		eventBus.$emit('notification:show', generateNotificationError(err));
	}

	beforeMount() {
		this.init();
	}

	async clear() {
		this.clearAllAlertModal = false;
		const result = await this.$store.dispatch('emulationProject/clearAll');
		if (!result) {
			eventBus.$emit('notification:show', generateNotificationError(this.clearProjectErrorMessage));
		}
		this.createEnvironmentPayload = null;
		await this.$router.push(ROUTES.EMULATION_PROJECT.ROOT);
	}

}

